import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { Box, MenuItem, Paper, TextField, Typography } from "@mui/material";
import { FormCore } from "src/Pages/components";
import { useContextReportAniversariantes } from "src/Contexts/ReportAniversariantes/ContextReportAniversariantes";
import { IFilterAniversariantes } from "src/Contexts/ReportAniversariantes/ContextReportAniversariantesTypes";

const ANO_FIXO = "2000";

const MESES = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];
const DIAS = Array.from({ length: 31 }, (_, i) =>
  String(i + 1).padStart(2, "0"),
);

/** Nome do mês em pt-BR com inicial maiúscula. */
const nomeMes = (mes: string) => {
  const nome = new Date(2000, Number(mes) - 1, 1).toLocaleString("pt-BR", {
    month: "long",
  });
  return nome.charAt(0).toUpperCase() + nome.slice(1);
};

/** Extrai dia e mês de YYYY-MM-DD. */
const parseDM = (s: string | undefined) => {
  if (!s || s.length < 10) return { dia: "01", mes: "01" };
  const [, mes, dia] = s.split("-");
  return { dia: dia ?? "01", mes: mes ?? "01" };
};

const toDateStr = (dia: string, mes: string) => `${ANO_FIXO}-${mes}-${dia}`;

interface IFormFilterAniversariantes {
  onFilterSubmit?: () => void;
}

const fieldSx = {
  minWidth: 72,
  "& .MuiOutlinedInput-root": {
    borderRadius: 1,
    "& fieldset": { borderRadius: 1 },
  },
} as const;

/** Campos de dia com largura máxima 200px. */
const fieldDiaSx = { ...fieldSx, maxWidth: 200 };

/** Altura máxima do dropdown (lista) dos selects de dia e mês. */
const menuPropsMaxHeight = {
  PaperProps: { sx: { maxHeight: 450 } },
};

const blockSx = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexWrap: "wrap",
  py: 0.5,
  px: 1,
  borderRadius: 1,
} as const;

export const FormFilterAniversariantes = ({
  onFilterSubmit,
}: IFormFilterAniversariantes) => {
  const { filterAniversariantes, setFilterAniversariantes } =
    useContextReportAniversariantes();
  const [loading, setLoading] = useState(false);

  const { di, mi, df, mf } = useMemo(() => {
    const i = parseDM(filterAniversariantes?.startDate);
    const f = parseDM(filterAniversariantes?.endDate);
    return { di: i.dia, mi: i.mes, df: f.dia, mf: f.mes };
  }, [filterAniversariantes?.startDate, filterAniversariantes?.endDate]);

  const handleInicial =
    (campo: "dia" | "mes") => (e: ChangeEvent<HTMLInputElement>) => {
      const dia = campo === "dia" ? e.target.value : di;
      const mes = campo === "mes" ? e.target.value : mi;
      setFilterAniversariantes((prev: IFilterAniversariantes) => ({
        ...prev,
        startDate: toDateStr(dia, mes),
      }));
    };

  const handleFinal =
    (campo: "dia" | "mes") => (e: ChangeEvent<HTMLInputElement>) => {
      const dia = campo === "dia" ? e.target.value : df;
      const mes = campo === "mes" ? e.target.value : mf;
      setFilterAniversariantes((prev: IFilterAniversariantes) => ({
        ...prev,
        endDate: toDateStr(dia, mes),
      }));
    };

  const handles = () => {
    setLoading(true);
    onFilterSubmit?.();
  };

  useEffect(() => {
    if (loading) {
      const t = setTimeout(() => setLoading(false), 800);
      return () => clearTimeout(t);
    }
  }, [loading]);

  return (
    <FormCore
      isNotCancel
      handleSubmit={handles}
      marginTopButtons="0"
      sxStyle={{
        margin: 0,
        marginTop: { xs: "8px", md: 0 },
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "nowrap",
        gap: 1,
      }}
      titleSubmit="Filtrar"
      loadingSubmit={loading}
    >
      <Paper
        elevation={0}
        variant="outlined"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          flexWrap: "nowrap",
          gap: 0,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: "divider",
          overflowX: "auto", // <--- permite scroll quando apertar
          overflowY: "hidden",
          px: 1.5,
          py: 1,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "stretch",
            flexWrap: "nowrap",
            width: "max-content", // <--- define largura real para não “forçar” quebra
            minWidth: "100%",
            gap: 0,
          }}
        >
          <Box
            sx={{
              ...blockSx,
              flex: "0 0 auto",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: 600, minWidth: 20 }}
            >
              De
            </Typography>
            <TextField
              size="small"
              select
              label="Dia"
              value={di}
              onChange={handleInicial("dia")}
              variant="outlined"
              sx={fieldDiaSx}
              SelectProps={{ MenuProps: menuPropsMaxHeight }}
              inputProps={{ "aria-label": "Dia inicial" }}
            >
              {DIAS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Mês"
              value={mi}
              onChange={handleInicial("mes")}
              variant="outlined"
              sx={{ ...fieldSx, minWidth: 96 }} // <--- reduz pra caber na linha
              SelectProps={{ MenuProps: menuPropsMaxHeight }}
              inputProps={{ "aria-label": "Mês inicial" }}
            >
              {MESES.map((m) => (
                <MenuItem key={m} value={m}>
                  {nomeMes(m)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
          <Box
            sx={{
              ...blockSx,
              flex: "0 0 auto",
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ fontWeight: 600, minWidth: 20 }}
            >
              Até
            </Typography>
            <TextField
              size="small"
              select
              label="Dia"
              value={df}
              onChange={handleFinal("dia")}
              variant="outlined"
              sx={fieldDiaSx}
              SelectProps={{ MenuProps: menuPropsMaxHeight }}
              inputProps={{ "aria-label": "Dia final" }}
            >
              {DIAS.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size="small"
              select
              label="Mês"
              value={mf}
              onChange={handleFinal("mes")}
              variant="outlined"
              sx={{ ...fieldSx, minWidth: 96 }} // <--- reduz pra caber na linha
              SelectProps={{ MenuProps: menuPropsMaxHeight }}
              inputProps={{ "aria-label": "Mês final" }}
            >
              {MESES.map((m) => (
                <MenuItem key={m} value={m}>
                  {nomeMes(m)}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
      </Paper>
    </FormCore>
  );
};
